import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { v4 as uuid } from 'uuid';
import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import * as Sentry from '@sentry/browser';

import { useDebounce, useI18n, useLogger, useSelector } from '~Hooks';

import {
  CreateInvoiceCustomer,
  CreateInvoiceCustomerVariables,
  GetAccountingInvoiceCustomers,
  GetAccountingInvoiceCustomersVariables,
  GetCustomers,
  GetCustomersVariables,
  UsesNewAccounting,
} from './__queries__';
import { IntegrationsCustomerType } from '../../../../__queries__/globalTypes';
import { MethodProps } from '../types';
import { useStorage } from '~Components/Storage';
import NumberInput from '~ComponentsImproved/Numpad';
import moment from 'moment';

type CustomerType = IntegrationsCustomerType.COMPANY | IntegrationsCustomerType.PRIVATE;

const Wrapper = styled.div`
  width: 100vw;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 49;
  animation: fade-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const InnerWrapper = styled.div<{
  top?: number;
}>`
  position: absolute;
  top: ${(props) => props.top || 70}px;
  width: 400px;
  left: calc(50% - 200px);
  height: 100px;
  padding: 20px;
  transition: all 200ms ease-out;
  animation: fade-and-translate-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const EditWrapper = styled.div`
  background: #fff;
  border-radius: 6px;
  position: absolute;
  top: 30px;
  width: 420px;
  left: calc(50% - 210px);
  max-height: calc(100vh - 60px);
  overflow-y: auto;
  transition: all 200ms ease-out;
  animation: fade-and-translate-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const Input = styled.input`
  box-sizing: border-box;
  font-size: 21px;
  padding: 15px;
  width: 400px;
  border: 0;
  outline: 0;
  margin: 0;
  border-radius: 2px;
  position: relative;
  z-index: 2;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.9);
`;

const FormInput = styled.input`
  box-sizing: border-box;
  font-size: 16px;
  padding: 12px 14px;
  width: 100%;
  border: 1px solid #ddd;
  outline: 0;
  border-radius: 4px;
  background: #f8f8f8;

  &:focus {
    border-color: #465fe6;
    background: #fff;
  }
`;

const FormRow = styled.div`
  padding: 0 16px;
  margin-bottom: 10px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin-bottom: 4px;
`;

const FormRowInline = styled.div`
  display: flex;
  gap: 10px;
  padding: 0 16px;
  margin-bottom: 10px;
`;

const ConfirmScreen = styled.div`
  color: #fff;
  margin-top: 200px;
  font-size: 30px;
  text-align: center;
  animation: fade-and-translate-in 400ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const CreateNewButton = styled.div<{
  disabled?: boolean;
}>`
  box-sizing: border-box;
  background: #465fe6;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.15);
  border-radius: 3px;
  padding: 15px 30px;
  width: 380px;
  color: #fff;
  margin: 10px;
  font-size: 22px;
  text-align: center;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
`;

const TightButton = styled.div<{
  disabled?: boolean;
}>`
  box-sizing: border-box;
  background: #665555;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.15);
  border-radius: 3px;
  padding: 8px 30px;
  color: #fff;
  width: 380px;
  margin: 10px;
  font-size: 17px;
  text-align: center;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
`;

const FormButton = styled.div<{
  disabled?: boolean;
  variant?: 'primary' | 'secondary';
}>`
  box-sizing: border-box;
  background: ${(props) => (props.variant === 'secondary' ? '#665555' : '#465fe6')};
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  padding: 14px;
  margin: 16px;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
`;

const TypeToggle = styled.div`
  display: flex;
  margin: 16px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #ddd;
`;

const TypeOption = styled.div<{ active: boolean }>`
  flex: 1;
  padding: 10px;
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  background: ${(props) => (props.active ? '#465fe6' : '#f8f8f8')};
  color: ${(props) => (props.active ? '#fff' : '#555')};
  transition: all 150ms ease;
`;

const Result = styled.div<{
  disabled?: boolean;
}>`
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.8);
  color: #333;
  font-size: 19px;
  padding: 15px;
  width: 380px;
  position: relative;
  z-index: 1;
  margin: 0 10px;
  margin-bottom: 1px;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
`;

const Name = styled.div`
  font-weight: bold;
`;

const Address = styled.div`
  font-size: 15px;
  opacity: 0.8;
`;

const Header = styled.div`
  background: #262626;
  box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.2);
  padding: 14px;
  font-size: 20px;
  color: #fff;
  text-align: center;
  font-weight: bold;
  border-radius: 6px 6px 0 0;
`;

const Icon = styled.i`
  font-size: 111px;
  text-shadow: 0px 0px 10px rgba(0, 0, 0, 0.5);
  color: #3f3;
`;

const InfoRow = styled.div`
  margin: 10px;
  text-align: left;
  color: #fff;
`;

const InfoTitle = styled.div`
  font-size: 14px;
`;

const InfoValue = styled.div`
  font-size: 18px;
  font-weight: bold;
`;

const ThinInput = styled.input`
  box-sizing: border-box;
  font-size: 16px;
  padding: 15px;
  width: calc(100% - 20px);
  border: 0;
  outline: 0;
  margin: 10px;
  border-radius: 2px;
  position: relative;
  z-index: 2;
  border-bottom: 1px solid #ccc;
  background: #f1f1f1;
`;

const CREATE_INVOICE_CUSTOMER = gql`
  mutation CreateInvoiceCustomer(
    $name: String!
    $companyId: String!
    $venueId: String!
    $email: String!
    $phone: String!
    $address1: String!
    $address2: String!
    $city: String!
    $zip: String!
    $provider: String!
    $orgNumber: String!
    $type: IntegrationsCustomerType!
    $externalId: String
  ) {
    createInvoiceCustomer(
      externalId: $externalId
      name: $name
      companyId: $companyId
      venueId: $venueId
      provider: $provider
      type: $type
      email: $email
      phone: $phone
      address1: $address1
      address2: $address2
      city: $city
      zip: $zip
      orgNumber: $orgNumber
    ) {
      success
      customer {
        id
      }
    }
  }
`;

const GET_CUSTOMERS = gql`
  query GetCustomers($venueId: String!, $search: String) {
    getCustomers(venueId: $venueId, search: $search, limit: 3) {
      items {
        id
        lastSeenActive
        name
        orgNumber
        provider
        email
        companyId
        phone
        address1
        address2
        city
        zip
        externalId
      }
    }
  }
`;

// True when the venue invoices through the accounting service, whose customer
// registry then replaces the integrations service's customers
const USES_NEW_ACCOUNTING = gql`
  query UsesNewAccounting {
    operativeViewer {
      id
      usesNewAccounting
    }
  }
`;

const GET_ACCOUNTING_INVOICE_CUSTOMERS = gql`
  query GetAccountingInvoiceCustomers($search: String, $limit: Int) {
    operativeViewer {
      id
      invoiceCustomers(search: $search, limit: $limit) {
        id
        name
        orgNumber
        email
        phone
        address1
        address2
        city
        zip
        externalId
        active
      }
    }
  }
`;

const InvoicePayment: React.FunctionComponent<MethodProps> = ({
  payment,
  discount,
  amount,
  onComplete,
  onMetaChange,
  onFail,
  venueId,
}) => {
  const [search, setSearch] = useState('');
  const [refName, setRefName] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const companyId = useSelector((state) => state.company);
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);
  const logger = useLogger('payment:invoice');
  const { i18n } = useI18n();
  const [{ enableTipping, enableInvoiceCustomerCreation }] = useStorage();
  const country = useSelector((state) => state.country);
  const [showTipModal, setShowTipModal] = useState(!payment.disableTip && enableTipping);

  // Create customer form state
  const [customerType, setCustomerType] = useState<CustomerType>(IntegrationsCustomerType.COMPANY);
  const [formName, setFormName] = useState('');
  const [formOrgNumber, setFormOrgNumber] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formAddress1, setFormAddress1] = useState('');
  const [formAddress2, setFormAddress2] = useState('');
  const [formCity, setFormCity] = useState('');
  const [formZip, setFormZip] = useState('');
  const [createdCustomer, setCreatedCustomer] = useState<{
    id: string;
    name: string;
    orgNumber: string;
    email: string;
    phone: string;
    address1: string;
    address2: string;
    city: string;
    zip: string;
  } | null>(null);

  const [createCustomer] = useMutation<CreateInvoiceCustomer, CreateInvoiceCustomerVariables>(
    CREATE_INVOICE_CUSTOMER,
  );

  // Prevent a backend request with every keystroke
  const debouncedSearchValue = useDebounce(search, 400);

  const newAccountingQuery = useQuery<UsesNewAccounting>(USES_NEW_ACCOUNTING, {
    fetchPolicy: 'network-only',
    // So a retry after a failure shows as loading again
    notifyOnNetworkStatusChange: true,
  });

  // Wait for the answer so the wrong registry never flashes by. If the
  // question fails, no registry is guessed: the staff gets a retry button.
  const usesNewAccounting = newAccountingQuery.data?.operativeViewer?.usesNewAccounting ?? false;
  const accountingFailed = !newAccountingQuery.loading && !!newAccountingQuery.error;
  const accountingDecided = !newAccountingQuery.loading && !newAccountingQuery.error;

  useEffect(() => {
    if (accountingFailed) {
      logger.error('Could not tell whether the venue uses new accounting', {
        errorObject: newAccountingQuery.error,
      });
    } else if (accountingDecided) {
      logger.info('Invoice customer registry chosen', {
        registry: usesNewAccounting ? 'accounting' : 'integrations',
      });
    }
  }, [accountingFailed, accountingDecided]);

  // The integrations service's customers, for venues not on new accounting
  const legacyCustomersQuery = useQuery<
    GetCustomers,
    GetCustomersVariables
  >(GET_CUSTOMERS, {
    variables: {
      venueId,
      search: debouncedSearchValue,
    },
    fetchPolicy: 'network-only',
    skip: !accountingDecided || usesNewAccounting,
  });

  // The accounting service's copy of the provider's (Fortnox, Spiris, Oqto) customer registry
  const accountingCustomersQuery = useQuery<
    GetAccountingInvoiceCustomers,
    GetAccountingInvoiceCustomersVariables
  >(GET_ACCOUNTING_INVOICE_CUSTOMERS, {
    variables: {
      search: debouncedSearchValue,
      // The search filters rows after DynamoDB counts them toward the limit,
      // so a small limit with a search pages through the registry a few rows
      // at a time. Only the empty search can ask for just the top three.
      limit: debouncedSearchValue ? 500 : 3,
    },
    fetchPolicy: 'network-only',
    skip: !accountingDecided || !usesNewAccounting,
  });

  const customersLoading =
    newAccountingQuery.loading ||
    (accountingDecided &&
      (usesNewAccounting ? accountingCustomersQuery.loading : legacyCustomersQuery.loading));

  useEffect(() => {
    setTimeout(() => {
      if (!selectedTargetId && input.current) {
        input.current.focus();
      }
    }, 100);
  }, [selectedTargetId]);

  /* This is inside an effect so we can cancel it
   * if user presses "undo"
   */
  useEffect(() => {
    if (!selectedTargetId || !confirmed) {
      return;
    }

    const id = setTimeout(() => {
      onComplete(undefined, {
        transactionId: uuid(),
        invoiceTarget: selectedTargetId,
        invoiceTargetName: refName.length > 0 ? refName : null,
      });
    }, 2000);

    return () => clearTimeout(id);
  }, [selectedTargetId, confirmed]);

  const canCreate = formName.trim().length > 0 && formOrgNumber.trim().length > 0;

  async function handleCreate() {
    if (loading || !canCreate) {
      return;
    }

    setLoading(true);
    logger.info('Creating invoice customer', { customerType, name: formName });

    try {
      const provider = country === 'NO' ? 'external' : 'fortnox';
      const externalId = provider === 'external' ? '<manual-invoice>' : undefined;

      const results = await createCustomer({
        variables: {
          name: formName.trim(),
          orgNumber: formOrgNumber.trim(),
          email: formEmail.trim(),
          phone: formPhone.trim(),
          address1: formAddress1.trim(),
          address2: formAddress2.trim(),
          city: formCity.trim(),
          zip: formZip.trim(),
          companyId,
          venueId,
          provider,
          type: customerType,
          externalId,
        },
        refetchQueries: ['GetCustomers'],
        awaitRefetchQueries: true,
      });

      const id = results.data?.createInvoiceCustomer?.customer?.id;
      if (id) {
        logger.info('Invoice customer created', { customerId: id });
        setCreatedCustomer({
          id,
          name: formName.trim(),
          orgNumber: formOrgNumber.trim(),
          email: formEmail.trim(),
          phone: formPhone.trim(),
          address1: formAddress1.trim(),
          address2: formAddress2.trim(),
          city: formCity.trim(),
          zip: formZip.trim(),
        });
        setSelectedTargetId(id);
        setShowEdit(false);
      }
    } catch (e) {
      logger.error('Invoice customer creation failed', { errorObject: e });
    }

    setLoading(false);
  }

  if (payment.status !== 'PENDING') {
    return null;
  }

  const mappedItems = usesNewAccounting
    ? (accountingCustomersQuery.data?.operativeViewer?.invoiceCustomers || []).map((item) => ({
        id: item.id,
        name: item.name,
        email: item.email,
        orgNumber: item.orgNumber,
        address1: item.address1,
        address2: item.address2,
        city: item.city,
        zip: item.zip,
        phone: item.phone,
        externalId: item.externalId,
        active: item.active,
      }))
    : (legacyCustomersQuery.data?.getCustomers.items || []).map((item) => ({
        id: item.id,
        name: item.name,
        email: item.email,
        orgNumber: item.orgNumber,
        address1: item.address1,
        address2: item.address2,
        city: item.city,
        zip: item.zip,
        phone: item.phone,
        externalId: item.externalId,
        // A Fortnox customer not seen for 32 hours has been removed in Fortnox
        active:
          item.provider !== 'fortnox' ||
          moment(item.lastSeenActive).isAfter(moment().subtract(32, 'hours')),
      }));

  const selectedTarget =
    mappedItems.find((target) => target.id === selectedTargetId) || createdCustomer;

  const targets =
    mappedItems
      .filter((target) => {
        return (
          target.name.toLowerCase().includes(search) ||
          target.orgNumber?.toLowerCase().includes(search) ||
          target.email?.toLowerCase().includes(search) ||
          target.externalId?.toLowerCase().includes(search)
        );
      })
      .slice(0, 3) || [];

  if (showTipModal) {
    return (
      <NumberInput
        key="tip"
        initialValue={'0'}
        decimal
        title={i18n('TM.Tip')}
        onSubmit={(tipAmount) => {
          setShowTipModal(false);

          // Ensure input is valid
          const tip = parseFloat(tipAmount);
          if (isNaN(tip)) {
            onFail();
            return;
          }

          onMetaChange({ tip: tip * 100 });

          // Slight delay so meta has time to update
          setTimeout(() => setShowTipModal(false), 1000);
        }}
        onClose={() => {
          setShowTipModal(false);
          onFail(i18n('Payment.Canceled'));
        }}
      />
    );
  }

  return (
    <Wrapper
      className="background"
      onTouchStart={(e) => {
        if (!loading && (e.target as HTMLDivElement).className.includes('background')) {
          onFail();
        }
      }}>
      {selectedTargetId && selectedTarget && confirmed ? (
        <InnerWrapper>
          <ConfirmScreen>
            <Icon className="fa fa-check-circle" />
            <br />
            <br />
            {i18n('Invoice.OrderRegistered')} <b>{selectedTarget.name}</b>
            <TightButton
              onClick={() => {
                setSelectedTargetId(null);
                setConfirmed(false);
              }}>
              {i18n('Invoice.Undo')}
            </TightButton>
          </ConfirmScreen>
        </InnerWrapper>
      ) : selectedTargetId && selectedTarget ? (
        <InnerWrapper key="confirm">
          <InfoRow>
            <InfoTitle>{i18n('Invoice.Name')}:</InfoTitle>
            <InfoValue>
              {selectedTarget.name || '--'} ({selectedTarget.orgNumber || '--'})
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoTitle>{i18n('Invoice.Phone')}:</InfoTitle>
            <InfoValue>{selectedTarget.phone || '--'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoTitle>{i18n('Invoice.Email')}:</InfoTitle>
            <InfoValue>{selectedTarget.email || '--'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoTitle>{i18n('Invoice.Adress')}:</InfoTitle>
            <InfoValue>
              {selectedTarget.address1} {selectedTarget.zip}
              <div>{selectedTarget.city}</div>
              <div>{selectedTarget.address2}</div>
            </InfoValue>
          </InfoRow>

          <ThinInput
            value={refName}
            placeholder={i18n('Invoice.Reference')}
            onChange={(e) => setRefName(e.target.value)}
          />
          <TightButton onClick={() => setConfirmed(true)}>{i18n('Invoice.Confirm')}</TightButton>
          <TightButton onClick={() => setSelectedTargetId(null)}>
            {i18n('Invoice.Cancel')}
          </TightButton>
        </InnerWrapper>
      ) : showEdit ? (
        <EditWrapper>
          <Header>{i18n('Invoice.CreateNewCustomer')}</Header>

          <TypeToggle>
            <TypeOption
              active={customerType === IntegrationsCustomerType.COMPANY}
              onClick={() => setCustomerType(IntegrationsCustomerType.COMPANY)}>
              {i18n('Invoice.Company')}
            </TypeOption>
            <TypeOption
              active={customerType === IntegrationsCustomerType.PRIVATE}
              onClick={() => setCustomerType(IntegrationsCustomerType.PRIVATE)}>
              {i18n('CustomerForCheckout.CustomerType.Private')}
            </TypeOption>
          </TypeToggle>

          <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
            <FormRow>
              <FormLabel>{i18n('Invoice.Name')} *</FormLabel>
              <FormInput
                value={formName}
                type="text"
                inputMode="text"
                autoCorrect="off"
                autoCapitalize="words"
                spellCheck={false}
                required
                onChange={(e) => setFormName(e.target.value)}
              />
            </FormRow>

            <FormRow>
              <FormLabel>
                {customerType === IntegrationsCustomerType.PRIVATE
                  ? i18n('Invoice.PersonalNumber')
                  : i18n('Invoice.OrgNo')}{' '}
                *
              </FormLabel>
              <FormInput
                value={formOrgNumber}
                type="text"
                inputMode="text"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                required
                onChange={(e) => setFormOrgNumber(e.target.value)}
              />
            </FormRow>

            <FormRow>
              <FormLabel>{i18n('Invoice.Email')}</FormLabel>
              <FormInput
                value={formEmail}
                type="email"
                inputMode="email"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                onChange={(e) => setFormEmail(e.target.value)}
              />
            </FormRow>

            <FormRow>
              <FormLabel>{i18n('Invoice.Phone')}</FormLabel>
              <FormInput
                value={formPhone}
                type="tel"
                inputMode="tel"
                autoCorrect="off"
                onChange={(e) => setFormPhone(e.target.value)}
              />
            </FormRow>

            <FormRow>
              <FormLabel>{i18n('Invoice.Adress')}</FormLabel>
              <FormInput
                value={formAddress1}
                type="text"
                inputMode="text"
                autoCorrect="off"
                spellCheck={false}
                placeholder={i18n('Invoice.Adress') + ' 1'}
                onChange={(e) => setFormAddress1(e.target.value)}
              />
            </FormRow>

            <FormRow>
              <FormInput
                value={formAddress2}
                type="text"
                inputMode="text"
                autoCorrect="off"
                spellCheck={false}
                placeholder={i18n('Invoice.Adress') + ' 2'}
                onChange={(e) => setFormAddress2(e.target.value)}
              />
            </FormRow>

            <FormRowInline>
              <div style={{ flex: 1 }}>
                <FormLabel>{i18n('Invoice.ZipCode')}</FormLabel>
                <FormInput
                  value={formZip}
                  type="text"
                  inputMode="numeric"
                  autoCorrect="off"
                  spellCheck={false}
                  onChange={(e) => setFormZip(e.target.value)}
                />
              </div>
              <div style={{ flex: 2 }}>
                <FormLabel>{i18n('Invoice.City')}</FormLabel>
                <FormInput
                  value={formCity}
                  type="text"
                  inputMode="text"
                  autoCorrect="off"
                  spellCheck={false}
                  onChange={(e) => setFormCity(e.target.value)}
                />
              </div>
            </FormRowInline>
          </form>

          <FormButton disabled={loading || !canCreate} onClick={handleCreate}>
            {loading ? i18n('Invoice.Creating') + '...' : i18n('Invoice.Save')}
          </FormButton>
          <FormButton
            variant="secondary"
            disabled={loading}
            onClick={() => !loading && setShowEdit(false)}>
            {i18n('Invoice.Cancel')}
          </FormButton>
        </EditWrapper>
      ) : (
        <InnerWrapper>
          <Input
            ref={input}
            value={search}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            onChange={(e) => setSearch(e.target.value.toLowerCase())}
            placeholder={i18n('Invoice.SearchUser')}
          />

          {customersLoading && (
            <Result>
              <Name>{i18n('Adyen.PleaseWait')}</Name>
              <Address>{i18n('General.Loading')}</Address>
            </Result>
          )}

          {accountingFailed && (
            <>
              <Result disabled>
                <Name>{i18n('Invoice.CustomersLoadFailed')}</Name>
              </Result>
              <CreateNewButton
                onClick={() => {
                  logger.info('Retrying the new accounting check');
                  newAccountingQuery.refetch().catch(() => {
                    // The error lands on the query and is shown and logged from there
                  });
                }}>
                {i18n('General.Retry')}
              </CreateNewButton>
            </>
          )}

          {!customersLoading &&
            targets.map((target) => (
              <Result
                key={target.id}
                disabled={!target.active}
                onClick={() => {
                  if (!target.active) {
                    return;
                  }

                  setSelectedTargetId(target.id);
                }}>
                <Name>
                  {target.name} {!target.active && '(inactive)'}
                </Name>
                <Address>{target.orgNumber}</Address>
              </Result>
            ))}
          {/* The accounting provider owns its customer registry, so customers are added there */}
          {enableInvoiceCustomerCreation && accountingDecided && !usesNewAccounting && (
            <CreateNewButton
              onClick={() => {
                setShowEdit(true);
              }}>
              {i18n('Invoice.CreateNewCustomer')}
            </CreateNewButton>
          )}
        </InnerWrapper>
      )}
    </Wrapper>
  );
};

export default InvoicePayment;
